
import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { AngularServicesContext } from 'react-app';
import { useDispatch } from 'react-redux';

// redux
import { QuizQuestionResponse, QuizQuestionType } from 'redux/schemas/models/quiz';
import { wrapThunkAction } from 'redux/utils';
import { editNumberAnswer, editQuizQuestionOption } from 'redux/actions/quizzes';

import ClickableContainer from 'components/clickable-container';
import useQuizModeAndQuestionType from 'quizzes/hooks/use-quiz-mode-and-question-type';
import NvIcon from 'shared/components/nv-icon';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import { black, gray7, primary } from 'styles/global_defaults/colors';
import { useFormContext } from 'react-hook-form';
import { LecturePageMode } from 'lecture_pages/components';
import NvPopover from 'shared/components/nv-popover';
import ResponsivelyEmbeddedAngularHTML from 'shared/components/responsively-embedded-angular-html';
import NvTooltip from 'shared/components/nv-tooltip';
import { CellMode } from './table-cell';
import quizContext, { QuestionContext, SavingIndicator } from '../../context';
import { config } from '../../../../../config/config.json';
import CorrectAnswer from '../../correct-answer';
import { removeDecimalForInteger } from '../../utils';

type AnswerCellProps = {
  handleSwitch: Function;
  showPopover: boolean;
  setShowPopover: Function;
  setShortAnswer: Function;
  isAnswerInRange: boolean;
  setIsAnswerInRange: Function;
  shortAnswer: string;
  childrenQuestions?: number[];
  tableQuestionId?: number;
  rowNumber: number;
  columnNumber: number;
};

const feedbackPopoverStyle = css`
  .fr-element p:first-of-type {
    margin-bottom: 0px;
  }
`;

const feedbackOverlayStyle = css`
  min-width: min-content;
`;

const getStyles = (isAnswerFeedbackPresent: boolean) => (
  css`
    background-color: ${gray7};
    .answer {
      width: 90%;
      color: ${black};
    }
    .answer:focus {
      outline: none !important;
    }
    .icon-container {
      display: none;
    }

    &:focus-within,
    &:hover {
      .icon-container {
        display: flex;
      }
    }
    &:focus-within {
      ${!isAnswerFeedbackPresent && css`
          border: 1px solid ${primary};
      `}
    }
    .answer-input {
      height: 100%;
      input {
        border: none;
      }
      .nv-text-input, .bs4-input-group, input{
        height: 100%;
      }
      input:focus {
        border: 1px solid ${primary};
      }
    }
  `
);

const AnswerCell = (props: AnswerCellProps) => {
  const {
    handleSwitch,
    showPopover,
    setShowPopover,
    isAnswerInRange,
    setIsAnswerInRange,
    shortAnswer,
    setShortAnswer,
    childrenQuestions,
    tableQuestionId,
    rowNumber,
    columnNumber,
  } = props;

  const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);
  const [isAnswerSet, setIsAnswerSet] = useState(false);

  const { isEditMode } = useQuizModeAndQuestionType();
  const { currentQuestion, responseOptions } = useContext(QuestionContext);
  const angularServices = React.useContext(AngularServicesContext);
  const {
    isHardDeadlineExpired,
    isContentManagementCollection,
    lecturePageMode,
    currentQuestionResponse,
    showCorrectAnswers,
    setSavingStatus,
    savingIndicatorTimeoutRef,
  } = useContext(quizContext);

  const dispatch = useDispatch();
  const { optionContent, topOfRange, bottomOfRange } = responseOptions[0];

  useEffect(() => {
    if (bottomOfRange && topOfRange && bottomOfRange !== topOfRange) {
      setIsAnswerInRange(true);
    }
  }, [bottomOfRange, responseOptions, setIsAnswerInRange, topOfRange]);

  // if answer for the respective child question is present, need to set isAnswerSet state to true
  useEffect(() => {
    switch (currentQuestion.type) {
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
      {
        if (optionContent) {
          setIsAnswerSet(true);
        } else {
          setIsAnswerSet(false);
        }
        break;
      }
      case QuizQuestionType.NUMBER_QUESTION:
      {
        if (topOfRange || bottomOfRange) {
          setIsAnswerSet(true);
        } else {
          setIsAnswerSet(false);
        }
        break;
      }

      default:
        break;
    }
  }, [
    bottomOfRange,
    currentQuestion.type,
    optionContent,
    responseOptions,
    topOfRange,
  ]);

  const isDisabled = lecturePageMode !== LecturePageMode.VIEW
    || isHardDeadlineExpired
    || isContentManagementCollection;

  const childQuestionIndex = childrenQuestions?.findIndex((eachId) => eachId === currentQuestion.id);
  const { setValue, trigger, getValues } = useFormContext() || {};

  const currentChildQuestionResponse: QuizQuestionResponse = currentQuestionResponse?.childrenQuestionResponses?.filter((eachChildResponse) => (eachChildResponse.questionId === currentQuestion.id))[0];

  useEffect(() => {
    if (!currentChildQuestionResponse) {
      setShowFeedbackPopover(false);
    }
  }, [currentChildQuestionResponse]);

  const getChildAnswerValue = () => {
    if (getValues(tableQuestionId?.toString())) {
      return getValues(tableQuestionId?.toString())[childQuestionIndex];
    }

    return null;
  };

  const handleAnswerChange = (e) => {
    setValue(tableQuestionId.toString(), {
      ...(getValues(tableQuestionId.toString())),
      [childQuestionIndex]: e.target.value,
    });

    trigger(tableQuestionId.toString());
  };

  const getInputType = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
        return 'text';
      case QuizQuestionType.NUMBER_QUESTION:
        return 'number';
      default:
        return 'text';
    }
  };

  const getAnswerFeedback = () => {
    if (showCorrectAnswers) {
      return (
        <CorrectAnswer />
      );
    }
    const getTemplate = () => {
      if (currentChildQuestionResponse.isCorrect) {
        if (currentChildQuestionResponse.feedback[0].explanation) {
          return currentChildQuestionResponse.feedback[0].explanation;
        }
        return t.SHARED.CORRECT();
      }
      if (currentChildQuestionResponse.feedback[0].secondaryExplanation) {
        return currentChildQuestionResponse.feedback[0].secondaryExplanation;
      }
      return t.SHARED.INCORRECT();
    };

    return (
      <div css={feedbackPopoverStyle} className={`d-flex ${currentChildQuestionResponse.isCorrect ? 'text-success' : 'text-danger'}`}>
        <NvIcon
          size='small d-flex align-items-center'
          icon={currentChildQuestionResponse.isCorrect ? 'complete' : 'error'}
        />
        <ResponsivelyEmbeddedAngularHTML
          className={`ml-3 mb-0 fr-element froala-style-medium ${currentChildQuestionResponse.isCorrect ? 'text-success' : 'text-danger'}`}
          angularServices={angularServices}
          template={getTemplate()}
        />
      </div>
    );
  };

  const getAnswerModeCellContent = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.NUMBER_QUESTION:
        // if number answer is set in range
        if (isAnswerInRange) {
          // no bottom and top range is set
          if (!topOfRange && !bottomOfRange) {
            return t.QUIZZES.TABLE_QUESTION.CORRECT_RANGE();
          }
          // if both range is set
          if (topOfRange && bottomOfRange) {
            return `${removeDecimalForInteger(bottomOfRange)} - ${removeDecimalForInteger(topOfRange)}`;
          }
          // if only one of range is set

          // if only top range is set
          if (topOfRange) {
            return removeDecimalForInteger(topOfRange);
          }

          // if only bottom range is set
          return removeDecimalForInteger(bottomOfRange);
        }
        // is answer is not in range
        if (!bottomOfRange) {
          return t.QUIZZES.NUMBER_ANSWER_QUESTION.CORRECT_NUMBER();
        }
        return removeDecimalForInteger(bottomOfRange);
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
        if (!shortAnswer) {
          return t.QUIZZES.SHORT_ANSWER_QUESTION.CORRECT_ANSWER_PLACEHOLDER();
        }
        return shortAnswer;
      default:
        return t.QUIZZES.SHORT_ANSWER_QUESTION.CORRECT_ANSWER_PLACEHOLDER();
    }
  };

  const resetShortAnswer = () => {
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    setShortAnswer('');
    wrapThunkAction(dispatch(editQuizQuestionOption(
      { id: responseOptions[0].id,
        patch: { optionContent: '' },
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const resetNumberAnswer = () => {
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(editNumberAnswer(
      {
        questionOptionId: responseOptions[0].id,
        bottomOfRange: null,
        topOfRange: null,
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const resetAnswer = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
        resetShortAnswer();
        break;
      case QuizQuestionType.NUMBER_QUESTION:
        resetNumberAnswer();
        break;
      default:
        break;
    }
  };

  const handleIconAction = () => {
    if (isAnswerSet) {
      resetAnswer();
    } else {
      setIsAnswerInRange(false);
      handleSwitch(CellMode.READONLY_MODE);
    }
  };

  if (isEditMode) {
    return (
      <div css={getStyles(false)} className='answer-container d-flex h-100'>
        <NvTooltip text={t.QUIZZES.TABLE_QUESTION.ANSWER_SET_INFO()}>
          <ClickableContainer
            onClick={() => setShowPopover(!showPopover)}
            className='answer py-3 pl-2 h-100 align-content-center d-block'
          >
            {getAnswerModeCellContent()}
          </ClickableContainer>
        </NvTooltip>
        <div className='icon-container ml-auto align-items-center pr-3'>
          <NvTooltip text={isAnswerSet ? t.QUIZZES.TABLE_QUESTION.CLEAR_INPUT() : t.QUIZZES.TABLE_QUESTION.CELL_SWITCH_TO_READONLY()}>
            <NvIcon
              className={`${isAnswerSet ? 'text-gray-2' : 'text-primary'} d-flex align-items-center`}
              size='small'
              icon={isAnswerSet ? 'close' : 'editable'}
              onClick={handleIconAction}
              data-qa={config.pendo.activities.quiz.questionModal.tableQuestion.switchToReadonlyMode}
            />
          </NvTooltip>
        </div>
      </div>
    );
  }

  return (
    <div css={getStyles(!!currentChildQuestionResponse)} className='w-100 h-100'>
      {currentChildQuestionResponse ? (
        <div className='d-flex h-100 align-items-center py-2'>
          <p className='mb-0 pl-2'>{currentChildQuestionResponse?.response}</p>
          <div className='ml-auto pr-3'>
            <NvPopover
              placement='top'
              show={showFeedbackPopover}
              content={getAnswerFeedback()}
              rootClose
              onHide={() => setShowFeedbackPopover(false)}
              overlayStyles={feedbackOverlayStyle}
            >
              <NvIcon
                size='small'
                className={`${currentChildQuestionResponse.isCorrect ? 'text-success' : 'text-danger'}`}
                icon={currentChildQuestionResponse.isCorrect ? 'complete' : 'error'}
                onClick={() => setShowFeedbackPopover(!showFeedbackPopover)}
              />
            </NvPopover>
          </div>
        </div>
      ) : (
        <NvTextInput
          type={getInputType()}
          className='answer-input'
          disabled={isDisabled}
          maxLength={255}
          value={getChildAnswerValue()}
          onChange={handleAnswerChange}
          ariaLabel={t.QUIZZES.TABLE_QUESTION.ANSWER_INPUT_ARIA_LABEL(columnNumber.toString(), rowNumber.toString())}
        />
      )}
    </div>
  );
};

export default AnswerCell;
